import type {
  QuizResponse,
  QuizFormData,
  QuizQuestionInputType,
  QuizQuestionAnswer,
  QuizUserAffiliation,
} from "../quiz.types";
import { TEXT_ANSWER_TYPES, FILE_ANSWER_TYPES, AFFILIATION_ANSWER_TYPES } from "../quiz.types";
import { affiliationsToOrgRoleEntries, type UserOrganizationAffiliation } from "@core/features/affiliations";
import { makeQuizQuestionInputKey } from "./makeQuizQuestionInputKey";

/**
 * Extracts form default values from a quiz response.
 * Maps answered questions to their form field names and values.
 */
export function packFormDefaultValuesFromQuiz(
  quiz: QuizResponse
): QuizFormData {
  if (!quiz || !quiz.questions) return {};

  const result: QuizFormData = {};

  const answeredQuestions = quiz.questions.filter((q) => q.answered);

  for (const q of answeredQuestions) {
    // Clean up old user_answer format (used to be strings before converted to ids)
    const cleanedAnswer = q.user_answer?.filter(
      (answer) => typeof answer === "number"
    );

    let value: QuizQuestionAnswer;

    // Check for affiliation answer (organization selector questions)
    if (
      AFFILIATION_ANSWER_TYPES.includes(q.type as QuizQuestionInputType) &&
      q.user_affiliations &&
      q.user_affiliations.length > 0
    ) {
      // Convert full affiliation objects back to AffiliationEntry[] for the form
      // QuizFormData types this as QuizQuestionAnswer but the form uses AffiliationEntry[]
      const affiliations: UserOrganizationAffiliation[] = q.user_affiliations.map(
        (a: QuizUserAffiliation) => ({ ...a, is_used_in_quiz: a.is_used_in_quiz ?? false })
      );
      value = affiliationsToOrgRoleEntries(affiliations) as unknown as QuizQuestionAnswer;
    }
    // Check for text answer (presence of user_answer_text takes precedence)
    else if (q.user_answer_text) {
      value = q.user_answer_text;
    }
    // Check for file/media answer
    else if (q.user_uploaded_media && q.user_uploaded_media.length > 0) {
      // Extract just the IDs from media objects for the form value
      // The full media objects are passed via initialFiles prop
      value = q.user_uploaded_media.map((media) => media.id);
    }
    // Check for choice-based answer
    else if (cleanedAnswer && cleanedAnswer.length > 0) {
      value = cleanedAnswer;
    }
    // Fallback based on type (for empty answers that should still be included)
    else if (AFFILIATION_ANSWER_TYPES.includes(q.type as QuizQuestionInputType)) {
      value = [] as unknown as QuizQuestionAnswer;
    } else if (TEXT_ANSWER_TYPES.includes(q.type as QuizQuestionInputType)) {
      value = q.user_answer_text ?? "";
    } else if (FILE_ANSWER_TYPES.includes(q.type as QuizQuestionInputType)) {
      value = [];
    } else {
      value = cleanedAnswer ?? [];
    }

    result[makeQuizQuestionInputKey(q.id)] = value;
  }

  return result;
}

